import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle2, AlertCircle, X, RefreshCw, FileText } from 'lucide-react';

interface EvidenceItem {
  domain: string;
  status: 'DOCUMENTED' | 'MISSING' | 'INSUFFICIENT';
  details: string;
}

interface MissingEvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: string;
  clientName: string;
  docType: string;
  reportingPeriod?: string;
}

export const MissingEvidenceModal: React.FC<MissingEvidenceModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  docType,
  reportingPeriod
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[]>([]);
  const [summary, setSummary] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;

    if (!clientId) {
      setError('Please select a client in the document header to audit chart grounding evidence.');
      setEvidenceItems([]);
      return;
    }

    setLoading(true);
    setError(null);

    fetch('/api/clinical/docs/grounding-evidence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId,
        docType,
        reportingPeriod
      })
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to audit grounding evidence');
        }
        return res.json();
      })
      .then((data) => {
        setEvidenceItems(data.missingEvidence || []);
        setSummary(data.summary || '');
      })
      .catch((err: any) => {
        console.error('Grounding evidence audit error:', err);
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, clientId, docType, reportingPeriod]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-4 bg-slate-950/90 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white flex items-center space-x-2">
                <span>Chart Grounding Evidence Review</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold border border-sky-500/30">
                  {docType}
                </span>
              </h3>
              <p className="text-[11px] text-slate-400">
                Authoritative chart evidence audit for <strong className="text-slate-300">{clientName}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 overflow-y-auto space-y-4 text-xs text-slate-200">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-400 space-y-3">
              <RefreshCw className="w-6 h-6 animate-spin text-sky-400" />
              <span>Auditing client encounters, attendance logs, and goal trials...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-amber-950/30 border border-amber-500/30 text-amber-200 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-bold text-xs">Notice</span>
                <p className="text-[11px] leading-relaxed">{error}</p>
              </div>
            </div>
          ) : (
            <>
              {summary && (
                <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Audit Outcome:</span>
                  <span className="font-bold text-slate-200 font-mono">{summary}</span>
                </div>
              )}

              <div className="space-y-3">
                {evidenceItems.map((item, idx) => {
                  const isMissing = item.status === 'MISSING';
                  const isInsufficient = item.status === 'INSUFFICIENT';
                  return (
                    <div
                      key={idx}
                      className={`p-3.5 rounded-xl border transition-colors ${
                        isMissing
                          ? 'bg-amber-950/20 border-amber-500/30 text-amber-100'
                          : isInsufficient
                          ? 'bg-yellow-950/20 border-yellow-500/30 text-yellow-100'
                          : 'bg-emerald-950/20 border-emerald-500/30 text-emerald-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-bold text-xs text-white flex items-center space-x-2">
                          {isMissing ? (
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          ) : isInsufficient ? (
                            <AlertCircle className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          )}
                          <span>{item.domain}</span>
                        </span>
                        <span
                          className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded border ${
                            isMissing
                              ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                              : isInsufficient
                              ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                              : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-300 leading-relaxed pl-5">
                        {item.details}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Grounding Contract Disclaimer */}
              <div className="p-3 rounded-xl bg-sky-950/20 border border-sky-500/20 text-[11px] text-sky-300 space-y-1 leading-relaxed">
                <div className="font-bold flex items-center space-x-1.5 text-sky-200">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>Clinical Grounding Contract Enforcement</span>
                </div>
                <p className="text-[10px] text-slate-300">
                  Under Allegiant Therapy safety rules, missing evidence is strictly preserved as missing. The AI Clinical Assist will never hallucinate attendance, invent cueing changes, or extrapolate unverified test scores.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3.5 bg-slate-950/90 border-t border-slate-800 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors cursor-pointer"
          >
            Close Audit
          </button>
        </div>
      </div>
    </div>
  );
};
